@if (loading()) {
  <div class="loading-container">
    <p-progressSpinner></p-progressSpinner>
    <p>Loading transcriptions...</p>
  </div>
} @else {
  <p-toast></p-toast>
  <p-dialog
    [visible]="deleteConfirmVisible()"
    (visibleChange)="onDeleteConfirmVisibleChange($event)"
    header="Delete transcription?"
    [modal]="true"
    [closable]="true"
    [dismissableMask]="true"
    [draggable]="false"
    [style]="{ width: 'min(420px, 92vw)' }"
    styleClass="delete-confirm-dialog">
    <div class="delete-confirm-dialog-body">
      <p class="delete-confirm-dialog-lead">
        Delete transcription for <strong>"{{ deleteConfirmPatientLabel() }}"</strong>?
      </p>
      <p class="delete-confirm-dialog-warning">This cannot be undone.</p>
    </div>
    <ng-template pTemplate="footer">
      <div class="delete-confirm-dialog-footer">
        <button
          type="button"
          pButton
          label="Cancel"
          class="p-button-text"
          (click)="cancelDeleteConfirm()"></button>
        <button
          type="button"
          pButton
          label="Delete"
          severity="danger"
          [disabled]="!pendingDeleteTranscription() || !!deletingRowId"
          (click)="confirmDeleteTranscription()"></button>
      </div>
    </ng-template>
  </p-dialog>
  <div class="transcriptions-container">
    <!-- Page Header -->
    <div class="page-header">
      <h1 class="page-title">All Transcriptions</h1>
    </div>

    <!-- Main Content Container -->
    <div class="main-content-container">
      <!-- Search and Filters Section -->
      <div class="search-filters-section">
        <div class="search-box">
          <i class="pi pi-search search-icon"></i>
          <input 
            type="text" 
            placeholder="Search transcriptions..."
            [(ngModel)]="searchTermValue"
            (input)="onSearchInput($event)"
            class="search-input">
        </div>
        
        <div class="filter-dropdowns">
          <p-select
            inputId="transcriptions-filter-status"
            class="filter-pselect"
            [options]="statusOptions"
            [(ngModel)]="selectedStatusValue"
            (ngModelChange)="onStatusFilterModelChange()"
            optionLabel="label"
            optionValue="value"
            placeholder="All Status"
            [appendTo]="'body'"
            panelStyleClass="transcriptions-filter-select-panel">
          </p-select>
          @if (!isNarrowLayout()) {
            <p-select
              inputId="transcriptions-filter-specialty"
              class="filter-pselect"
              [options]="specialtyOptions"
              [(ngModel)]="selectedSpecialtyValue"
              (ngModelChange)="onSpecialtyFilterModelChange()"
              optionLabel="label"
              optionValue="value"
              placeholder="All Specialties"
              [appendTo]="'body'"
              panelStyleClass="transcriptions-filter-select-panel">
            </p-select>
          }
          <button 
            (click)="onResetFilters()"
            class="reset-button"
            type="button">
            <i class="pi pi-refresh"></i>
            Reset
          </button>
        </div>
      </div>

      @if (!isNarrowLayout()) {
      <!-- Table Section (Desktop) -->
      <div class="table-section">
      <div class="table-header-with-refresh">
        <h2 class="table-title">Transcriptions</h2>
        <button 
          class="refresh-btn"
          (click)="refreshTranscriptions()"
          [disabled]="isRefreshing()"
          title="Refresh transcriptions">
          <i class="pi" [ngClass]="{'pi-refresh': !isRefreshing(), 'pi-spin pi-spinner': isRefreshing()}"></i>
          {{ isRefreshing() ? 'Refreshing...' : 'Refresh' }}
        </button>
      </div>
      
      <p-table 
        [value]="tableRowsForDisplay()" 
        [paginator]="true" 
        [rows]="10"
        [showCurrentPageReport]="true"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        styleClass="p-datatable-sm"
        class="transcriptions-table">
        
        <ng-template pTemplate="header">
          <tr>
            <th>Patient</th>
            <th>MRN</th>
            <th>Date & Time</th>
            <th>Duration</th>
            <th>Status</th>
            <th class="transcriptions-actions-header" scope="col" aria-label="Actions"></th>
          </tr>
        </ng-template>
        
        <ng-template pTemplate="body" let-transcription>
          @if (isUploadSpinnerRow(transcription)) {
            <tr class="upload-spinner-row">
              <td colspan="6">
                <div class="upload-spinner-cell">
                  <p-progressSpinner strokeWidth="4" [style]="{ width: '32px', height: '32px' }"></p-progressSpinner>
                  <span>Uploading recording…</span>
                </div>
              </td>
            </tr>
          } @else {
            <tr
              class="transcription-row"
              [class.transcription-row-clickable]="canOpenTranscription(transcription)"
              (click)="onViewTranscription(transcription.id)">
              <td>
                @if (isEditingRow(transcription)) {
                  <div class="inline-edit-block" (click)="$event.stopPropagation()">
                    <div class="inline-edit-field">
                      <p-autoComplete
                        [(ngModel)]="editingPatientName"
                        [suggestions]="editingPatientSuggestions"
                        (completeMethod)="onEditPatientSearch($event)"
                        (onSelect)="onEditPatientSelect($event.value)"
                        (ngModelChange)="onEditPatientNameChange()"
                        optionLabel="nameOnly"
                        optionValue="nameOnly"
                        appendTo="self"
                        panelStyleClass="inline-edit-autocomplete-panel"
                        inputStyleClass="inline-edit-input"
                        styleClass="inline-edit-autocomplete"
                        [delay]="0"
                        [minLength]="0"
                        [showEmptyMessage]="false"
                        [dropdown]="false"
                        placeholder="Type patient name">
                      </p-autoComplete>
                    </div>
                    <div class="inline-edit-actions">
                      <button
                        type="button"
                        class="inline-btn icon-btn save-btn"
                        title="Save"
                        aria-label="Save"
                        [disabled]="isSavingRow(transcription)"
                        (click)="saveRowEdit(transcription, $event)">
                        {{ isSavingRow(transcription) ? '…' : '✓' }}
                      </button>
                      <button
                        type="button"
                        class="inline-btn icon-btn cancel-btn"
                        title="Cancel"
                        aria-label="Cancel"
                        [disabled]="isSavingRow(transcription)"
                        (click)="cancelRowEdit($event)">
                        ✕
                      </button>
                    </div>
                  </div>
                } @else {
                  <div class="patient-cell-content">
                    <div class="patient-cell-text">
                      <a 
                        class="patient-link"
                        (click)="onPatientClick(transcription.mrn, $event)">
                        {{ transcription.patientName }}
                      </a>
                      @if (isUnknownPatient(transcription)) {
                        <button
                          type="button"
                          class="unknown-edit-trigger"
                          title="Edit unknown patient"
                          aria-label="Edit unknown patient"
                          (click)="startRowEdit(transcription, $event)">
                          ✎
                        </button>
                      }
                    </div>
                  </div>
                }
              </td>
              <td>{{ transcription.mrn }}</td>
              <td>{{ formatDateTime(transcription) }}</td>
              <td>{{ transcription.duration }}</td>
              <td>
                <div class="status-cell-with-spinner">
                  <p-tag 
                    [value]="getDisplayStatus(transcription.status)" 
                    [severity]="getStatusSeverity(transcription.status)"
                    [rounded]="true"
                    [ngClass]="{
                      'needs-review-tag': transcription.status?.toLowerCase() === 'needs review',
                      'in-progress-tag': ['in progress', 'transcribing', 'transcribed', 'analyzing'].includes(transcription.status?.toLowerCase())
                    }">
                  </p-tag>
                </div>
              </td>
              <td class="transcriptions-actions-cell" (click)="$event.stopPropagation()">
                <div class="transcriptions-actions-bar">
                  <div class="transcriptions-actions-bar-icons">
                    <div class="transcriptions-actions-leading">
                      <div class="transcriptions-row-actions">
                        @if (canOpenTranscription(transcription)) {
                          <button
                            type="button"
                            class="view-transcription-btn view-transcription-btn--actions"
                            [class.view-transcription-btn--soap-unread]="transcription.soapUnread"
                            [attr.title]="transcription.soapUnread ? 'SOAP not reviewed yet' : 'View transcription'"
                            [attr.aria-label]="transcription.soapUnread ? 'SOAP not reviewed yet — view transcription' : 'View transcription'"
                            (click)="onViewTranscription(transcription.id, $event)">
                            View
                          </button>
                        }
                        <div class="transcriptions-row-actions-delete">
                          <button
                            type="button"
                            class="delete-transcription-btn"
                            title="Delete transcription"
                            aria-label="Delete transcription"
                            [disabled]="isDeletingRow(transcription)"
                            (click)="onDeleteTranscription(transcription, $event)">
                            <i class="pi pi-trash" aria-hidden="true"></i>
                          </button>
                        </div>
                        @if (isPausedTranscription(transcription)) {
                          <button
                            type="button"
                            class="resume-recording-btn"
                            title="Resume recording"
                            aria-label="Resume recording"
                            (click)="goResumePausedTranscription(transcription, $event)">
                            <span class="resume-play-icon" aria-hidden="true">▶</span>
                          </button>
                        }
                        @if (isAppendMoreRecordingEligible(transcription)) {
                          <button
                            type="button"
                            class="append-recording-btn"
                            title="Add more recording to this visit"
                            aria-label="Add more recording to this visit"
                            (click)="goAppendMoreRecording(transcription, $event)">
                            <span class="append-recording-icon" aria-hidden="true">＋</span>
                          </button>
                        }
                      </div>
                    </div>
                  </div>
                  <div class="transcriptions-actions-spinner-slot" aria-live="polite">
                    @if (showStatusProgressSpinner(transcription)) {
                      <p-progressSpinner
                        strokeWidth="3"
                        aria-hidden="true"
                        [style]="{ width: '1.125rem', height: '1.125rem' }">
                      </p-progressSpinner>
                    }
                  </div>
                </div>
              </td>
            </tr>
          }
        </ng-template>
      </p-table>
    </div>
      }

      @if (isNarrowLayout()) {
    <!-- Mobile List View (iOS-style grouped rows) -->
    <div class="mobile-list-view">
      <h2 class="mobile-list-section-heading">Transcriptions</h2>

      @if (showRecordingUploadShim()) {
        <div class="patient-item patient-item-ios mobile-upload-spinner">
          <div class="upload-spinner-cell">
            <p-progressSpinner strokeWidth="4" [style]="{ width: '32px', height: '32px' }"></p-progressSpinner>
            <span>Uploading recording…</span>
          </div>
        </div>
      }
      @for (transcription of mobilePaginatedTranscriptions(); track transcription.id) {
        <div
          class="patient-item patient-item-ios"
          [class.patient-item-clickable]="canOpenTranscription(transcription)"
          (click)="onViewTranscription(transcription.id)">
          @if (isEditingRow(transcription)) {
            <div class="mobile-row-edit patient-item-ios-inner" (click)="$event.stopPropagation()">
              <div class="patient-ios-title-row">
                <div class="patient-name patient-ios-title-edit">
                  <p-autoComplete
                    [(ngModel)]="editingPatientName"
                    [suggestions]="editingPatientSuggestions"
                    (completeMethod)="onEditPatientSearch($event)"
                    (onSelect)="onEditPatientSelect($event.value)"
                    (ngModelChange)="onEditPatientNameChange()"
                    optionLabel="nameOnly"
                    optionValue="nameOnly"
                    appendTo="self"
                    panelStyleClass="inline-edit-autocomplete-panel"
                    inputStyleClass="inline-edit-input mobile-patient-name-input"
                    styleClass="inline-edit-autocomplete"
                    [delay]="0"
                    [minLength]="0"
                    [showEmptyMessage]="false"
                    [dropdown]="false"
                    placeholder="Patient name">
                  </p-autoComplete>
                </div>
              </div>
              <div class="mobile-inline-edit mobile-inline-edit-actions-only">
                <div class="inline-edit-actions">
                  <button
                    type="button"
                    class="inline-btn icon-btn save-btn"
                    title="Save"
                    aria-label="Save"
                    [disabled]="isSavingRow(transcription)"
                    (click)="saveRowEdit(transcription, $event)">
                    {{ isSavingRow(transcription) ? '…' : '✓' }}
                  </button>
                  <button
                    type="button"
                    class="inline-btn icon-btn cancel-btn"
                    title="Cancel"
                    aria-label="Cancel"
                    [disabled]="isSavingRow(transcription)"
                    (click)="cancelRowEdit($event)">
                    ✕
                  </button>
                </div>
              </div>
            </div>
          } @else {
            <div class="patient-item-ios-inner">
              <div class="patient-ios-title-row">
                <div class="patient-ios-title-block">
                  <div class="patient-cell-content patient-ios-name-wrap">
                    <div class="patient-cell-text">
                      <a
                        class="patient-link patient-ios-title-link"
                        [routerLink]="['/v0/patients', transcription.mrn]"
                        (click)="$event.stopPropagation()">
                        {{ transcription.patientName }}
                      </a>
                      @if (isUnknownPatient(transcription)) {
                        <button
                          type="button"
                          class="unknown-edit-trigger"
                          title="Edit unknown patient"
                          aria-label="Edit unknown patient"
                          (click)="startRowEdit(transcription, $event)">
                          ✎
                        </button>
                      }
                    </div>
                  </div>
                </div>
                @if (canOpenTranscription(transcription)) {
                  <button
                    type="button"
                    class="view-transcription-btn view-transcription-btn--mobile"
                    [class.view-transcription-btn--soap-unread]="transcription.soapUnread"
                    [attr.title]="transcription.soapUnread ? 'SOAP not reviewed yet — tap to view' : 'View transcription'"
                    [attr.aria-label]="transcription.soapUnread ? 'SOAP not reviewed yet — view transcription' : 'View transcription'"
                    (click)="$event.stopPropagation(); onViewTranscription(transcription.id)">
                    View
                  </button>
                }
              </div>
              <div class="patient-ios-meta-block" aria-label="Transcription details">
                <div class="patient-ios-mrn-row">
                  <span class="patient-ios-meta-label">MRN:</span>
                  <span class="patient-ios-mrn-value">{{ transcription.mrn }}</span>
                </div>
                <div class="patient-ios-date-duration-row">
                  <span class="patient-ios-date-line">
                    <span class="patient-ios-meta-label">Date:</span>
                    <span class="patient-ios-datetime">{{ formatDateTime(transcription) }}</span>
                  </span>
                  <span class="patient-ios-duration">{{ transcription.duration }}</span>
                </div>
              </div>
              <div class="patient-ios-footer-row" (click)="$event.stopPropagation()">
                <div class="patient-ios-status status-cell-with-spinner">
                  <p-tag
                    [value]="getDisplayStatus(transcription.status)"
                    [severity]="getStatusSeverity(transcription.status)"
                    [rounded]="true"
                    [ngClass]="{
                      'needs-review-tag': transcription.status?.toLowerCase() === 'needs review',
                      'in-progress-tag': ['in progress', 'transcribing', 'transcribed', 'analyzing'].includes(transcription.status?.toLowerCase())
                    }">
                  </p-tag>
                  @if (showStatusProgressSpinner(transcription)) {
                    <p-progressSpinner
                      strokeWidth="3"
                      aria-hidden="true"
                      class="patient-ios-footer-spinner"
                      [style]="{ width: '1.125rem', height: '1.125rem' }">
                    </p-progressSpinner>
                  }
                </div>
                <div class="patient-ios-actions">
                  <div class="transcriptions-row-actions patient-row-actions">
                    <div class="transcriptions-row-actions-delete">
                      <button
                        type="button"
                        class="delete-transcription-btn"
                        title="Delete transcription"
                        aria-label="Delete transcription"
                        [disabled]="isDeletingRow(transcription)"
                        (click)="onDeleteTranscription(transcription, $event)">
                        <i class="pi pi-trash" aria-hidden="true"></i>
                      </button>
                    </div>
                    <div class="transcriptions-row-actions-primary">
                      @if (isPausedTranscription(transcription)) {
                        <button
                          type="button"
                          class="resume-recording-btn"
                          title="Resume recording"
                          aria-label="Resume recording"
                          (click)="goResumePausedTranscription(transcription, $event)">
                          <span class="resume-play-icon" aria-hidden="true">▶</span>
                        </button>
                      }
                      @if (isAppendMoreRecordingEligible(transcription)) {
                        <button
                          type="button"
                          class="append-recording-btn"
                          title="Add more recording to this visit"
                          aria-label="Add more recording to this visit"
                          (click)="goAppendMoreRecording(transcription, $event)">
                          <span class="append-recording-icon" aria-hidden="true">＋</span>
                        </button>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      }
    </div>

    <!-- Mobile Pagination -->
    <div class="mobile-pagination">
      <div class="mobile-pagination-info">
        Showing {{ (mobileCurrentPage() * mobilePageSize) + 1 }} to {{ Math.min((mobileCurrentPage() + 1) * mobilePageSize, filteredTranscriptions().length) }} of {{ filteredTranscriptions().length }} entries
      </div>
      <div class="mobile-pagination-controls">
        <p-paginator 
          [rows]="mobilePageSize"
          [totalRecords]="filteredTranscriptions().length"
          [first]="mobileCurrentPage() * mobilePageSize"
          (onPageChange)="onMobilePageChange($event)"
          [showCurrentPageReport]="false">
        </p-paginator>
      </div>
    </div>
      }

    <!-- Statistics Section -->
    <div class="statistics-section">
      <div class="stats-card">
        <h3 class="stats-title">Transcription Statistics</h3>
        <div class="stats-grid">
          <div class="stat-item">
            <span class="stat-label">Total</span>
            <span class="stat-value">{{ totalRecords() }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">This Month</span>
            <span class="stat-value">{{ thisMonthCount() }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Avg Duration</span>
            <span class="stat-value">{{ averageDuration() }}</span>
          </div>
        </div>
      </div>

      <div class="stats-card">
        <h3 class="stats-title">By Specialty</h3>
        <div class="stats-grid">
          <div class="stat-item">
            <span class="stat-label">Epilepsy</span>
            <span class="stat-value">{{ specialtyStats().epilepsy }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Headache</span>
            <span class="stat-value">{{ specialtyStats().headache }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Movement Disorders</span>
            <span class="stat-value">{{ specialtyStats().movementDisorders }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Other</span>
            <span class="stat-value">{{ specialtyStats().generalNeurology + specialtyStats().stroke }}</span>
          </div>
        </div>
      </div>

      <div class="stats-card">
        <h3 class="stats-title">By Status</h3>
        <div class="stats-grid">
          <div class="stat-item">
            <span class="stat-label">Completed</span>
            <span class="stat-value">{{ statusStats().completed }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Needs Review</span>
            <span class="stat-value">{{ statusStats().needsReview }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">In Progress</span>
            <span class="stat-value">{{ statusStats().inProgress }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Failed</span>
            <span class="stat-value">{{ statusStats().failed }}</span>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
}
